import { createSelector } from 'reselect';
import { get, isEmpty } from 'lodash';
import moment from 'moment-timezone';
import { IState as IChartState } from './reducer';
import {
  SCOPE,
  CHART_SCOPE,
  ZOOM_CHART_REQUEST_SCOPE,
  BRUSH_CHART_REQUEST_SCOPE,
  BRUSH_COMPARE_CHART_REQUEST_SCOPE,
  SECONDARY_LINE_CHART_REQUEST_SCOPE,
  SECONDARY_COMPARE_LINE_CHART_REQUEST_SCOPE,
} from './types';
import { intervalSelector } from '@store/common/selectors';
import { getCompareIntervalForDimensionTable } from '@src/components/common/GranularitySelect/utils';
import { comparisonIntervalSelector } from '@store/summaryContainer/comparison/selectors';
export interface IInterval {
  from: string | number;
  to: string | number;
  granularity?: number;
}

const TIMESTAMP_DISPLAY_FORMAT = 'MMM DD HH:mm';

export const formatter = (ts: string | number) =>
  ts ? moment(ts).format(TIMESTAMP_DISPLAY_FORMAT) : '';

export const chartSelector = (state): IChartState => get(state, CHART_SCOPE);
export const zoomChartSelector = (state) => get(state, ZOOM_CHART_REQUEST_SCOPE);
export const brushChartSelector = (state) => get(state, BRUSH_CHART_REQUEST_SCOPE);
export const brushCompareChartSelector = (state) => get(state, BRUSH_COMPARE_CHART_REQUEST_SCOPE);
export const secondaryLineChartSelector = (state) => get(state, SECONDARY_LINE_CHART_REQUEST_SCOPE);
export const secondaryCompareLineChartSelector = (state) =>
  get(state, SECONDARY_COMPARE_LINE_CHART_REQUEST_SCOPE);

export const defaultValueSelector = createSelector(chartSelector, (state: IChartState) => ({
  defaultZoom: state.defaultZoom,
  defaultBrush: state.defaultBrush,
}));

export const zoomSelector = createSelector(chartSelector, (state: IChartState) => state.zoom);

export const brushSelector = createSelector(chartSelector, (state: IChartState) => state.brush);

export const zoomIntervalSelector = createSelector(intervalSelector, (interval: any) => ({
  from: interval.from,
  to: interval.to,
}));

export const brushIntervalSelector: any = createSelector(
  brushSelector,
  (brush: [number, number]) => ({
    from: brush[0],
    to: brush[1],
  }),
);

export const noBrushSelector: any = createSelector(brushIntervalSelector, (brush) => {
  const { from, to } = brush;
  return from === 0 && to === 0;
});

export const chartIntervalSelector: any = createSelector(
  intervalSelector,
  zoomIntervalSelector,
  brushIntervalSelector,
  (interval: any, zoomInterval: any, brushInterval: any) => {
    const { from, to, granularity } = interval;

    const { from: zoomFrom, to: zoomTo } = zoomInterval;
    const { from: brushFrom, to: brushTo } = brushInterval;

    if (brushFrom && brushTo) {
      return {
        from: brushFrom,
        to: brushTo,
        granularity,
      };
    }

    if (zoomFrom && zoomTo) {
      return {
        from: zoomFrom,
        to: zoomTo,
        granularity,
      };
    }

    return {
      from,
      to,
      granularity,
    };
  },
);

export const tableCompareIntervalSelector: any = createSelector(
  chartIntervalSelector,
  noBrushSelector,
  comparisonIntervalSelector,
  (interval: any, noBrush: any, comparisonInterval: any) => {
    const dodEnabled = !isEmpty(comparisonInterval);
    const isChartSliced = !noBrush;
    let tableCompareInterval: any = null;
    if (dodEnabled) {
      tableCompareInterval = comparisonInterval;
    }
    if (isChartSliced) {
      tableCompareInterval = getCompareIntervalForDimensionTable(interval);
    }
    return tableCompareInterval;
  },
);

export const chartIntervalTextSelector: any = createSelector(
  chartIntervalSelector,
  ({ from, to }: any) => `${formatter(from)} - ${formatter(to)}`,
);

// Get the interval from bar chart
export const analysisIntervalSelector: any = createSelector(
  chartIntervalSelector,
  ({ from, to }: any) => [formatter(from), formatter(to)],
);

export const birdChartSharedSelector = createSelector(chartSelector, (store) => ({
  ...store,
  defaultZoom: store.zoom,
  defaultBrush: store.brush,
}));

// Root selector function for backward compatibility
const rootSelector = (state) => get(state, SCOPE);

// Named export for backward compatibility (old default export)
export const birdChartRootSelector = rootSelector;

// Object of selectors (new API)
const selectorsObject = {
  brushSelector,
  zoomSelector,
  chartSelector,
  intervalSelector,
  zoomIntervalSelector,
  brushIntervalSelector,
  noBrushSelector,
  chartIntervalSelector,
  tableCompareIntervalSelector,
  chartIntervalTextSelector,
  analysisIntervalSelector,
  birdChartSharedSelector,
  brushChartSelector,
  brushCompareChartSelector,
  secondaryLineChartSelector,
  secondaryCompareLineChartSelector,
  defaultValueSelector,
};

export default selectorsObject;
